import type { UserDataViewDto } from '@act/shared/data-transfer-objects';
import { createAsyncThunk, type SerializedError } from '@reduxjs/toolkit';
import { userRequests } from '../requests/users.request';
import { makeRequest, ApiRequestError } from '../utils/apiRequest';
import { setErrorToast } from '../utils/errorToast.util';
import { chunkArray } from '../utils/chunkArray.util';
import type { CustomSerializedError } from '../utils/apiThunk';

/** Max ids per GET /users request (must stay <= server GET_USERS_MAX_IDS = 25). */
export const GET_USERS_BY_IDS_CHUNK_SIZE = 25;

type GetUsersByIdsParams = {
  ids: string[];
  includeDetails?: boolean;
};

/**
 * Fetches users by id in chunks so a large missing-id set does not hit the
 * server ids cap or inflate a single response heap.
 */
export const getUsersByIds = createAsyncThunk<
  UserDataViewDto,
  { params?: GetUsersByIdsParams },
  { rejectValue: CustomSerializedError }
>('getUsersByIds', async (request, thunkAPI) => {
  const ids = request.params?.ids ?? [];
  const includeDetails = request.params?.includeDetails;
  const empty: UserDataViewDto = {
    users: [],
    memberStatusChangeRequests: [],
  };

  if (ids.length === 0) {
    return empty;
  }

  try {
    const batches = chunkArray(ids, GET_USERS_BY_IDS_CHUNK_SIZE);
    const parts: UserDataViewDto[] = [];
    for (const batch of batches) {
      const part = await makeRequest(userRequests.getUsersByIds, {
        params: { ids: batch, includeDetails },
      });
      parts.push(part);
    }

    return parts.reduce<UserDataViewDto>(
      (acc, part) => ({
        users: [...acc.users, ...part.users],
        memberStatusChangeRequests: [
          ...acc.memberStatusChangeRequests,
          ...part.memberStatusChangeRequests,
        ],
      }),
      empty
    );
  } catch (err) {
    const apiError = err as ApiRequestError;
    setErrorToast(apiError, thunkAPI.dispatch);
    const serializedError = err as SerializedError;
    return thunkAPI.rejectWithValue({
      ...serializedError,
      isAccessDenied: apiError.isAccessDenied,
      errorIsCaught: false,
    });
  }
});
